import { MyNaviTypography } from '@/components/Campaign/mynavi/MyNaviUI/MyNaviTypography'
import { IconChevronDown, IconChevronUp, IconSquareCheck, IconSquareCheckFilled } from '@tabler/icons-react'
import { useState } from 'react'

export type AccordionItem = {
  id: string | number
  label: string
  checked: boolean
  onToggle: () => void
}

export type AccordionGroup = {
  name: string
  /** 「すべて」行のチェック状態 */
  allChecked: boolean
  /** 「すべて」行クリック時のコールバック */
  onToggleAll: () => void
  items: AccordionItem[]
  defaultExpanded?: boolean
}

export type MyNaviFilterAccordionListProps = {
  groups: AccordionGroup[]
}

const FONT_STYLE = { fontFamily: "'BIZ UDPGothic', sans-serif" }

function CheckboxIcon({ checked }: { checked: boolean }) {
  return checked ? (
    <IconSquareCheckFilled size={16} color="#058bbe" className="flex-shrink-0" />
  ) : (
    <IconSquareCheck size={16} color="#686868" strokeWidth={1.5} className="flex-shrink-0" />
  )
}

export function MyNaviFilterAccordionList({ groups }: MyNaviFilterAccordionListProps) {
  const [prevGroups, setPrevGroups] = useState(groups)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(groups.filter((g) => g.defaultExpanded).map((g) => g.name)),
  )

  // groups が非同期で後から変化した場合（空→データあり）に defaultExpanded を反映する
  // useEffect 内での setState を避けるためレンダー中に状態調整する（React推奨パターン）
  if (groups !== prevGroups) {
    setPrevGroups(groups)
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      for (const group of groups) {
        if (group.defaultExpanded && !next.has(group.name)) {
          next.add(group.name)
        }
      }
      return next
    })
  }

  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupName)) {
        next.delete(groupName)
      } else {
        next.add(groupName)
      }
      return next
    })
  }

  return (
    <div className="flex flex-col">
      {groups.map((group) => {
        const isExpanded = expandedGroups.has(group.name)
        return (
          <div key={group.name}>
            {/* グループヘッダー行 */}
            <button
              type="button"
              onClick={() => toggleGroup(group.name)}
              className="flex items-center justify-between w-full h-[50px] px-4 bg-white border-b border-[#e5e5e5]"
            >
              <span className="text-[#686868] text-[14px] font-bold" style={FONT_STYLE}>
                {group.name}
              </span>
              {isExpanded ? <IconChevronUp size={20} color="#686868" /> : <IconChevronDown size={20} color="#686868" />}
            </button>

            {/* 展開時: すべて行 + アイテム行 */}
            {isExpanded && (
              <>
                {/* すべて行 */}
                <button
                  type="button"
                  onClick={group.onToggleAll}
                  className="flex items-center justify-between h-[38px] px-[17px] w-full bg-[#f8f8f8] border-b border-[#e5e5e5]"
                >
                  <MyNaviTypography variant="label">すべて</MyNaviTypography>
                  <CheckboxIcon checked={group.allChecked} />
                </button>

                {/* 個別アイテム行 */}
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={item.onToggle}
                    className="flex items-center justify-between h-[38px] px-[17px] w-full bg-[#f8f8f8] border-b border-[#e5e5e5]"
                  >
                    <MyNaviTypography variant="label">{item.label}</MyNaviTypography>
                    <CheckboxIcon checked={item.checked} />
                  </button>
                ))}
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}
